import { isValidPhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

import { ISFormInputField } from '@/shared/services/forms/forms.types';

import { checkIfUrlIsForParticularDomain, checkIfUrlIsValid } from './common';
import { FILE_TYPE_IDS } from './constants';

// login

export const loginEmailSchema = z.object({
	email: z
		.string()
		.trim()
		.min(1, { message: 'Email is required' })
		.email({ message: 'Invalid email' }),
});

export const loginOTPSchema = z.object({
	otp: z.string().length(6, { message: 'Invalid otp' }),
});

export const registerOTPSchema = z.object(loginOTPSchema.shape);

export type TLoginEmailSchema = z.infer<typeof loginEmailSchema>;
export type TLoginOTPSchema = z.infer<typeof loginOTPSchema>;

export interface ILoginEmailFormState {
	email: string;
	validator: typeof loginEmailSchema;
	error: string;
}

export interface ILoginOTPFormState {
	otp: string;
	validator: typeof loginOTPSchema;
	error: string;
}

export function generateFormSchemaFromInputFields(inputFields: ISFormInputField[]) {
	const schemaObject: Record<string, z.ZodTypeAny> = {};

	inputFields.forEach((inputField) => {
		let fieldSchema: z.ZodTypeAny;

		switch (inputField.type) {
			case 'SHORT_TEXT':
				fieldSchema = z
					.string({
						required_error: 'This field is required',
					})
					.max(250, { message: `Must be less than 250 characters` });
				if (inputField.preDefinedInputType === 'EMAIL') {
					fieldSchema = z
						.string({
							required_error: 'This field is required',
						})
						.trim()
						.email({ message: 'Invalid email format' });
				}
				break;

			case 'LONG_TEXT':
				fieldSchema = z
					.string({
						required_error: 'This field is required',
					})
					.max(5000, { message: 'Must be less than 5000 characters' });
				break;

			case 'NUMBER':
				fieldSchema = z.number({
					required_error: 'This field is required',
				});
				break;

			case 'LINK':
				if (inputField.preDefinedInputType === 'LINKEDIN') {
					fieldSchema = z
						.string({
							required_error: 'This field is required',
						})
						.refine(
							(val) => {
								const linkedinRegex = /^(https?:\/\/)?([\w-]+\.)*linkedin\.com\/[^\s]*$/i;
								if (!linkedinRegex.test(val)) return false;

								// Extract the path after linkedin.com/
								const urlMatch = val.match(/linkedin\.com\/([^?#]*)/i);
								if (!urlMatch) return false;

								const path = urlMatch[1].toLowerCase();

								// Block common non-profile paths
								const blockedPaths = [
									'', // Empty path
									'feed',
									'settings',
									'notifications',
									'messaging',
									'jobs',
									'learning',
									'groups',
									'events',
									'sales',
									'talent',
									'premium',
								];

								// Check if path starts with any blocked path
								for (const blockedPath of blockedPaths) {
									if (path === blockedPath || path.startsWith(blockedPath + '/')) {
										return false;
									}
								}

								return true;
							},
							{ message: 'Must be a LinkedIn profile URL. Eg. linkedin.com/in/john' }
						);
				} else if (inputField.preDefinedInputType === 'TWITTER') {
					fieldSchema = z
						.string({
							required_error: 'This field is required',
						})
						.refine(
							(val) => {
								return (
									checkIfUrlIsForParticularDomain(val, 'x.com') ||
									checkIfUrlIsForParticularDomain(val, 'twitter.com')
								);
							},
							{ message: 'Must be a X (Twitter) URL' }
						);
				} else if (inputField.preDefinedInputType === 'INSTAGRAM') {
					fieldSchema = z
						.string({
							required_error: 'This field is required',
						})
						.refine(
							(val) => {
								return checkIfUrlIsForParticularDomain(val, 'instagram.com');
							},
							{ message: 'Must be a Instagram URL' }
						);
				} else if (inputField.preDefinedInputType === 'GITHUB') {
					fieldSchema = z
						.string({
							required_error: 'This field is required',
						})
						.refine(
							(val) => {
								return checkIfUrlIsForParticularDomain(val, 'github.com');
							},
							{ message: 'Must be a GitHub URL' }
						);
				} else if (inputField.preDefinedInputType === 'YOUTUBE') {
					fieldSchema = z
						.string({
							required_error: 'This field is required',
						})
						.refine(
							(val) => {
								return (
									checkIfUrlIsForParticularDomain(val, 'youtube.com') ||
									checkIfUrlIsForParticularDomain(val, 'youtu.be')
								);
							},
							{ message: 'Must be a YouTube video URL' }
						);
				} else {
					fieldSchema = z.string().refine(checkIfUrlIsValid, { message: 'Invalid URL' });
				}
				break;

			case 'MULTI_SELECT':
				if (inputField.options && inputField.options.length > 0) {
					fieldSchema = z.array(z.string(), {
						required_error: 'This field is required',
					});
				} else {
					fieldSchema = z.array(z.string(), {
						required_error: 'This field is required',
					});
				}
				break;

			case 'SINGLE_SELECT':
				if (inputField.options && inputField.options.length > 0) {
					fieldSchema = z.string({
						required_error: 'This field is required',
					});
				} else {
					fieldSchema = z.string({
						required_error: 'This field is required',
					});
				}
				break;

			case 'DROPDOWN':
				if (inputField.options && inputField.options.length > 0) {
					fieldSchema = z.string({
						required_error: 'This field is required',
					});
				} else {
					fieldSchema = z.string({
						required_error: 'This field is required',
					});
				}
				break;

			case 'DATE':
				fieldSchema = z.string({
					required_error: 'This field is required',
				});
				break;

			case 'PHONE_NUMBER':
				fieldSchema = z
					.string({
						required_error: 'This field is required',
					})
					.refine(isValidPhoneNumber, { message: 'Invalid phone number' });
				break;

			case 'FILE_UPLOAD':
				fieldSchema = z.object(
					{
						contentType: z.enum(['NONE', ...FILE_TYPE_IDS]),
						draftFilePath: z.string(),
						originalFilename: z.string(),
					},
					{
						required_error: 'This field is required',
					}
				);
				break;

			case 'VIDEO':
				// Accept both old format (ISUploadFileDraftFileMetadata) and new format (IVideoFileMetadata)
				fieldSchema = z.union(
					[
						z.object({
							contentType: z.enum(['video/webm']),
							draftFilePath: z.string(),
							originalFilename: z.string(),
						}),
						z.object({
							file_path: z.string(),
							file_name: z.string(),
							duration: z.number().optional(),
						}),
					],
					{
						required_error: 'This field is required',
					}
				);
				break;

			default:
				fieldSchema = z.string({
					required_error: 'This field is required',
				});
		}

		// Make field optional if not required, otherwise ensure non-null values
		if (inputField.required) {
			fieldSchema = fieldSchema.nullish().refine((val) => val != null, {
				message: 'This field is required',
			});
		} else {
			fieldSchema = fieldSchema.nullish();
		}

		schemaObject[inputField.id] = fieldSchema;
	});
	return z.object(schemaObject);
}

export const nameSchema = z.object({
	name: z
		.string()
		.min(1, { message: 'This field is required' })
		.max(100, { message: 'Must be less than 100 characters' }),
});

export type TNameSchema = z.infer<typeof nameSchema>;

export const workspaceNameSchema = z.object({
	name: z
		.string()
		.trim()
		.min(2, { message: 'Workspace name must be at least 2 characters long' })
		.max(50, { message: 'Workspace name must be less than 50 characters' }),
});

export type TWorkspaceNameSchema = z.infer<typeof workspaceNameSchema>;

export interface INameFormState {
	name: string;
	validator: typeof nameSchema;
	error: string;
}

export const emailSchema = z.object({
	email: z.string().trim().email({ message: 'Invalid email' }),
});

export type TEmailSchema = z.infer<typeof emailSchema>;

export interface IEmailFormState {
	email: string;
	validator: typeof emailSchema;
	error: string;
}
